import React from 'react';
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import logoUrl from "@/assets/images/logo.svg";
import LottieModule from "lottie-react";
import lottieFile from "@/assets/error.json";
import Button from "@/components/Base/Button";

// lottie-react's UMD build gets mis-interopped by some bundlers, which resolve
// the default import to the whole CJS exports object instead of the Lottie
// component itself. Unwrap `.default` when that happens.
const Lottie = (LottieModule as unknown as { default?: typeof LottieModule }).default ?? LottieModule;

type ErrorFallbackProps = {
    error: Error;
    resetErrorBoundary: () => void;
};

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetErrorBoundary }) => {
    const { t } = useTranslation();
    return (
        <div
            className={clsx([
                "p-3 sm:px-8 relative h-screen lg:overflow-hidden  xl:bg-white dark:bg-darkmode-800 xl:dark:bg-darkmode-600",
                "before:hidden before:xl:block before:content-[''] before:w-[57%] before:-mt-[28%] before:-mb-[16%] before:-ml-[13%] before:absolute before:inset-y-0 before:left-0 before:transform before:rotate-[-4.5deg] before:bg-primary/20 before:rounded-[100%] before:dark:bg-darkmode-400",
                "after:hidden after:xl:block after:content-[''] after:w-[57%] after:-mt-[20%] after:-mb-[13%] after:-ml-[13%] after:absolute after:inset-y-0 after:left-0 after:transform after:rotate-[-4.5deg] after:bg-red-500 after:rounded-[100%] after:dark:bg-darkmode-700",
            ])}
        >
            {/*<ThemeSwitcher/>*/}
            <div className="container relative z-10 sm:px-10">
                <div className="block grid-cols-2 gap-4 xl:grid">
                    {/* BEGIN: Login Info */}
                    <div className="flex-col hidden min-h-screen xl:flex">
                        <a href="" className="flex items-center pt-5 -intro-x">
                            <img
                                alt="Shop"
                                className="w-6"
                                src={logoUrl}
                            />
                            <span className="ml-3 text-lg text-white"> {t("waiter")} </span>
                        </a>
                        <div className="my-auto mx-auto xl:mr-36 2xl:mr-48">
                            <Lottie animationData={lottieFile} loop={true}/>
                        </div>
                    </div>
                    {/* END: Login Info */}

                    {/* BEGIN: Login Form */}
                    <div className="flex flex-col justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
                        <div className="p-8 rounded-2xl text-center">
                            <h1 className="text-2xl xl:text-4xl font-bold text-red-600 mb-4">{t("somethingWentWrong")}</h1>
                            <p className="text-sm xl:text-lg text-gray-600 mb-4">{error.message}</p>
                            <Button
                                onClick={resetErrorBoundary}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 w-[8rem] h-[2.5rem] rounded-md transition duration-300"
                            >
                                {t("tryAgain")}
                            </Button>
                        </div>
                    </div>
                    {/* END: Login Form */}
                </div>
            </div>
        </div>

    );
};

export default ErrorFallback;
